import { apiClient } from "../../../services/apiService";
import {
  AnalyticsResponse,
  BetsResponse,
  CalendarResponse,
  DayBetsResponse,
  PerformanceFilters,
  PerformanceSummary,
  TrackedBetDto,
  filtersToParams,
} from "../performance/performanceApi";

/**
 * Client for the personal bet tracker API (/api/v1/my-bets/**).
 * Authenticated: every request is scoped server-side to the JWT user.
 * Read endpoints mirror the /api/v1/performance shapes so the calendar,
 * hero stats, and ledger components render user data unchanged.
 *
 * Request bodies are snake_case (project-wide backend Jackson strategy);
 * responses use camelCase map keys like the performance API.
 */

const BASE = "/api/v1/my-bets";

// ── Types ────────────────────────────────────────────────────────────

/** User summary: same aggregate block, no agent bankroll concepts. */
export type MyBetsSummary = Omit<
  PerformanceSummary,
  "startingBankroll" | "currentBankroll"
> & {
  startingBankroll?: number;
  currentBankroll?: number;
};

/**
 * What a board sends to track a bet. A board-native reference — the
 * server resolves full settlement identity from its own snapshot by
 * odd_id and falls back to these fields if the line rotated off.
 */
export interface TrackBetPayload {
  odd_id?: string | null;
  league: string;
  market_key?: string | null;
  market?: string | null;
  selection?: string | null;
  bet_type?: string | null;
  selection_name?: string | null;
  bookmaker_key: string;
  odds_american: number;
  stake_amount: number;
  event?: string | null;
  event_start_time?: string | null;
  ev_percent?: number | null;
  fair_odds_american?: number | null;
  /** BPI bets only. ev_percent stays the real EV. */
  bpi_ev_percent?: number | null;
  bpi_probability?: number | null;
  bpi_margin?: number | null;
  is_live?: boolean;
  /** Live boards only: ms since the quote's vendor last_update at click time. */
  quote_age_ms?: number | null;
  source: string;
  /**
   * Parlay legs (slip tracks only). The backend stores these and
   * expands them into per-leg fingerprint rows so each leg's board row
   * shows the "already bet" badge.
   */
  legs?: Array<{
    odd_id: string | null;
    market_key: string | null;
    selection_name: string;
    /** The leg's own price at the slip's book. Lets a voided leg drop
     *  out and the ticket re-price automatically. */
    odds_american?: number | null;
    /** Leg identity fallbacks for when the server's odds snapshot has
     *  already rotated at track time. */
    league?: string | null;
    event?: string | null;
    event_start_time?: string | null;
    market?: string | null;
  }> | null;
}

export interface TrackBetResult {
  created: boolean;
  autoGradable: boolean;
  bet: TrackedBetDto;
}

/** Light row backing the boards' "already bet" badges. */
export interface MyBetFingerprint {
  id: number;
  groupKey: string | null;
  oddId: string | null;
  marketType: string | null;
  marketTitle: string | null;
  selection: string | null;
  betType: string | null;
  bookmakerKey: string | null;
  status: string | null;
  settled: boolean;
  /** True for a parlay's expanded leg rows — badge-only, not a bet of
   *  its own, so open-bet counts must skip them. */
  leg?: boolean;
}

export type ManualGradeStatus = "WON" | "LOST" | "PUSH" | "VOID";

// ── Reads (null on error, matching the performance API contract) ────

async function get<T>(url: string): Promise<T | null> {
  try {
    const response = await apiClient.get(url);
    return response.data as T;
  } catch (error) {
    console.error("[my-bets] request failed", url, error);
    return null;
  }
}

export const fetchMyBetsSummary = (filters: PerformanceFilters = {}) =>
  get<MyBetsSummary>(`${BASE}/summary?${filtersToParams(filters)}`);

export const fetchMyBetsAnalytics = (filters: PerformanceFilters, groupBy: string) =>
  get<AnalyticsResponse>(`${BASE}/analytics?${filtersToParams(filters, { groupBy })}`);

/**
 * Every open bet, paging through the ledger so a heavy tracker never
 * silently loses rows past the first page.
 */
export async function fetchAllPendingBets(): Promise<TrackedBetDto[] | null> {
  const all: TrackedBetDto[] = [];
  for (let page = 0; page < 20; page++) {
    const res = await fetchMyBetsLedger({ status: "PENDING" }, page, 100, "eventStartTime", "asc");
    if (!res) return page === 0 ? null : all;
    all.push(...(res.bets ?? []));
    if (page + 1 >= (res.totalPages ?? 1)) break;
  }
  return all;
}

export const fetchMyBetsLedger = (
  filters: PerformanceFilters,
  page: number,
  size: number,
  sortBy = "placedAt",
  sortDir = "desc"
) =>
  get<BetsResponse>(
    `${BASE}/bets?${filtersToParams(filters, { page, size, sortBy, sortDir })}`
  );

export const fetchMyBetsCalendar = (month: string, filters: PerformanceFilters = {}) =>
  get<CalendarResponse>(`${BASE}/calendar?${filtersToParams(filters, { month })}`);

export const fetchMyBetsDayBets = (date: string, filters: PerformanceFilters = {}) =>
  get<DayBetsResponse>(`${BASE}/calendar/bets?${filtersToParams(filters, { date })}`);

export const fetchMyBetsFingerprints = () =>
  get<{ bets: MyBetFingerprint[] }>(`${BASE}/fingerprints`);

/**
 * Current in-play number per open bet while its game is live — a
 * prop's stat so far (RBIs hit, strikeouts thrown…) or a game
 * market's running score. Same providers that grade the bet later.
 */
export interface LiveProgressRow {
  betId: number;
  statValue: number | null;
  homeScore: number | null;
  awayScore: number | null;
  /** Game clock when the source carries one ("Top 5th", "7:32 - 3rd"). */
  phase: string | null;
  /** Current price for this exact side/line from the live event cache. */
  liveOddsAmerican?: number | null;
  /** One-price market-implied probability, including the book's vig. */
  liveProbabilityPercent?: number | null;
  liveOddsSportsbook?: string | null;
  liveOddsAgeMs?: number | null;
  /** Per-leg standings — parlays only. */
  legs?: LiveLeg[] | null;
}

/** One parlay leg's standing while the ticket is still open. */
export interface LiveLeg {
  selectionName: string;
  /** WON / LOST / PENDING / UPCOMING (not played yet) / UNKNOWN. */
  status: string;
  current: number | null;
  line: number | null;
}

export const fetchMyBetsLiveProgress = () =>
  get<{ bets: LiveProgressRow[] }>(`${BASE}/live-progress`);

// ── Mutations (throw with the server's message so the UI can toast) ─

function errorMessage(error: unknown): string {
  const data = (error as { response?: { data?: { error?: string } } })?.response?.data;
  return data?.error || "Something went wrong. Try again.";
}

export async function trackBet(payload: TrackBetPayload): Promise<TrackBetResult> {
  try {
    const response = await apiClient.post(BASE, payload);
    return response.data as TrackBetResult;
  } catch (error) {
    throw new Error(errorMessage(error));
  }
}

export async function editMyBet(
  id: number,
  changes: { stake_amount?: number; odds_american?: number }
): Promise<TrackedBetDto> {
  try {
    const response = await apiClient.patch(`${BASE}/${id}`, changes);
    return (response.data as { bet: TrackedBetDto }).bet;
  } catch (error) {
    throw new Error(errorMessage(error));
  }
}

export async function deleteMyBet(id: number): Promise<void> {
  try {
    await apiClient.delete(`${BASE}/${id}`);
  } catch (error) {
    throw new Error(errorMessage(error));
  }
}

export async function settleMyBet(
  id: number,
  status: ManualGradeStatus,
  actualResultValue?: number | null
): Promise<TrackedBetDto> {
  try {
    const response = await apiClient.post(`${BASE}/${id}/settle`, {
      status,
      actual_result_value: actualResultValue ?? null,
    });
    return (response.data as { bet: TrackedBetDto }).bet;
  } catch (error) {
    throw new Error(errorMessage(error));
  }
}
